import { ParamRelatorio } from '../interfaces/relatorios-agenda.dto';
import { campoDoCpf } from './relatorio-filtros.util';

export interface RelatorioRecente { id: string; valores: Record<string, string>; geradoEm: number; }

export const MAX_RECENTES = 6;
const CHAVE_SENSIVEL = /cpf|senha|password/i;
const DIA_MS = 86_400_000;

/** Prefixo `fila:` porque o clear() do logout só preserva chaves `fila:`. */
export function chaveRecentes(login: string): string {
  return `fila:relatorios-agenda:recentes:${login}`;
}

/** Corpo enviado sem nada que identifique o paciente (CPF e o campo oculto do PATIENT_ID) nem senha. */
export function valoresSeguros(params: ParamRelatorio[], corpo: Record<string, string>): Record<string, string> {
  const proibidos = new Set<string>();
  for (const p of params) {
    if (p.type === 'cpf-lookup') proibidos.add(p.name);
    const oculto = campoDoCpf(p);
    if (oculto) proibidos.add(oculto);
  }
  const v: Record<string, string> = {};
  for (const [k, valor] of Object.entries(corpo ?? {})) {
    if (proibidos.has(k) || CHAVE_SENSIVEL.test(k)) continue;
    v[k] = valor === undefined || valor === null ? '' : String(valor);
  }
  return v;
}

/** Mais recente primeiro, sem repetir relatório, no máximo MAX_RECENTES. */
export function registrarRecente(lista: RelatorioRecente[], novo: RelatorioRecente): RelatorioRecente[] {
  return [novo, ...lista.filter(r => r.id !== novo.id)].slice(0, MAX_RECENTES);
}

/** Lê o JSON salvo; qualquer coisa fora do formato vira lista vazia ou é descartada. */
export function lerRecentes(bruto: string | null | undefined): RelatorioRecente[] {
  if (!bruto) return [];
  let dados: unknown;
  try {
    dados = JSON.parse(bruto);
  } catch {
    return [];
  }
  if (!Array.isArray(dados)) return [];
  const validos: RelatorioRecente[] = [];
  for (const d of dados as any[]) {
    if (!d || typeof d !== 'object' || typeof d.id !== 'string' || !d.id || typeof d.geradoEm !== 'number' || !isFinite(d.geradoEm)) continue;
    const valores: Record<string, string> = {};
    if (d.valores && typeof d.valores === 'object' && !Array.isArray(d.valores)) {
      for (const [k, v] of Object.entries(d.valores)) if (typeof v === 'string' && !CHAVE_SENSIVEL.test(k)) valores[k] = v;
    }
    if (validos.some(r => r.id === d.id)) continue;
    validos.push({ id: d.id, valores, geradoEm: d.geradoEm });
  }
  return validos.slice(0, MAX_RECENTES);
}

function inicioDoDia(ms: number): number {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** "hoje 14:32", "ontem" ou "há N dias", por dia do calendário. */
export function rotuloQuando(geradoEm: number, agora: number): string {
  const dias = Math.round((inicioDoDia(agora) - inicioDoDia(geradoEm)) / DIA_MS);
  if (dias <= 0) {
    const d = new Date(geradoEm);
    return `hoje ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
  if (dias === 1) return 'ontem';
  return `há ${dias} dias`;
}
